import type { Map as ImmutableMap } from 'immutable';
import { OrderedMap } from 'immutable';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { dateSelectors } from 'app-components/flux/date';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { preferencesV2Selectors } from 'app-components/flux/preferences-v2';
import { transactionsSelectors } from 'app-components/flux/transactions';
import tracker from 'app-components/utils/core/tracker';

import CloseIcon from '@mui/icons-material/Close';
import SettingsSuggestOutlinedIcon from '@mui/icons-material/SettingsSuggestOutlined';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import SnackbarContent from '@mui/material/SnackbarContent';

import { showTransactionsSettingsDialog } from 'components/Dialogs/TransactionsSettingsDialog/actions';
import { addNotifications, removeNotifications } from 'data/notifications/notificationsActions';
import { mkNotification } from 'data/notifications/notificationsTypes';
import { getTTI } from 'utils/tti';

let showWarning = true;

const TransactionsTTI = () => {
  const dispatch = useDispatch();
  const transactions: ImmutableMap<string, any> = useSelector(transactionsSelectors.getResourcesById);
  const tti = getTTI();
  const currentDate: DateTime = useSelector(dateSelectors.getCurrentDate);
  const dateOnAfterPrefISO = useSelector((state) => preferencesV2Selectors.getPreferenceValue(state, 'dateOnAfter'));
  const dateOnAfterPref = useMemo(
    () => dateOnAfterPrefISO && DateTime.fromISO(dateOnAfterPrefISO),
    [dateOnAfterPrefISO],
  );
  const transactionsTTI: number = useSelector((state) =>
    // @ts-expect-error FIXME TS2345
    featureFlagsSelectors.getFeatureFlag(state, 'transactionsTti'),
  );
  const dateOnAfterDB = useSelector(transactionsSelectors.getDateOnAfter);
  useEffect(() => {
    if (
      showWarning &&
      tti > transactionsTTI &&
      dateOnAfterPrefISO === dateOnAfterDB && // check for temporary load
      dateOnAfterPref &&
      transactions.size > 1000 &&
      currentDate.year - dateOnAfterPref.year > 2
    ) {
      tracker.track(tracker.events.performanceWarning, {
        $duration: tti,
        tti,
        transactionsTTI,
        dateOnAfterPref,
        years: currentDate.year - dateOnAfterPref.year,
        transactionsSize: transactions.size,
      });
      const id = uuidv4();
      dispatch(
        addNotifications(
          OrderedMap({
            [id]: mkNotification({
              id,
              severity: 'warning',
              snackbarContent: SnackbarContent,
              autoHideDuration: undefined,
              snackbarContentProps: {
                message: (
                  <Button
                    size='small'
                    aria-label='optimize'
                    id='optimize-performance'
                    color='inherit'
                    onClick={() => {
                      dispatch(removeNotifications([id]));
                      dispatch(showTransactionsSettingsDialog());
                    }}
                  >
                    <SettingsSuggestOutlinedIcon />
                    &nbsp; Click to optimize performance
                  </Button>
                ),
                action: (
                  <IconButton
                    size='small'
                    aria-label='dismiss'
                    id='outdated-browser-dismiss'
                    color='inherit'
                    onClick={() => dispatch(removeNotifications([id]))}
                  >
                    <CloseIcon />
                  </IconButton>
                ),
              },
            }),
          }),
        ),
      );
      showWarning = false;
    }
  }, [transactions, tti, dateOnAfterPref, dateOnAfterPrefISO, dateOnAfterDB, currentDate, dispatch, transactionsTTI]);

  return null;
};

export default TransactionsTTI;
